/**
 * Fine-grained reactive signal primitives — the web-kit reactivity core (RFC §3a).
 *
 * Explicit-dependency reactivity: `signal()` (manual pub/sub, `Object.is` equality),
 * `computedFrom([deps], fn)` and `effect([deps], fn)`. There is NO implicit
 * auto-tracking runtime in v1; deps are always enumerated. (The prototype's
 * auto-tracking `computed()` is DELETED here: its docstring claimed dependency
 * tracking it never performed — it computed once, subscribed to nothing, and only
 * went dirty via a manual `recompute()`. See RFC §3a "What is proven vs. new".)
 *
 * ── Batch / notification semantics (NORMATIVE — pinned by the tests here) ──
 * There is ONE signal factory, and it is batch-aware. Outside `batch()`, `set()`
 * notifies synchronously (once per changed value). Inside `batch()`:
 *   - reads (`.value`) see the pending (latest-written) value immediately;
 *   - each signal defers and COALESCES to at most one notification, delivered with
 *     the signal's FINAL value when the outermost batch closes;
 *   - a net-zero change across the batch (final value equals the value subscribers
 *     last saw) emits NO notification.
 * Consequences for the derived primitives when several deps change in one batch:
 *   - `computedFrom` recomputes once per CHANGED dep on batch close, but only
 *     notifies its own subscribers when its OWN value changes — so it emits a
 *     single notification with the final derived value (equality dedup collapses
 *     the rest). It NEVER emits an intermediate value: because `set()` commits the
 *     read value immediately, every recompute during the flush already observes all
 *     deps at their final values, regardless of flush order.
 *   - `effect` has no equality dedup, so it RUNS once per changed dep on batch
 *     close — but every run observes all deps at their FINAL values (never an
 *     intermediate one). Collapsing those N runs into a single unit of work is the
 *     render scheduler's job (element-level dedupe, RFC §3a): `batch()` guarantees
 *     no intermediate values; the scheduler guarantees one flush. Both are required.
 */

export type Subscriber<T> = (value: T) => void;
export type Unsubscribe = () => void;

/**
 * A reactive signal holding a value of type T.
 * Subscribers are notified when the value changes.
 */
export interface Signal<T> {
  /** Current value (read-only access). Inside a batch, this is the pending value. */
  readonly value: T;

  /** Update the signal value. Notifies subscribers if the value changed. */
  set(value: T): void;

  /** Update the signal value using a function of the current value. */
  update(fn: (current: T) => T): void;

  /** Subscribe to value changes. Returns an unsubscribe function. */
  subscribe(fn: Subscriber<T>): Unsubscribe;

  /** Current subscriber count (useful for debugging/testing). */
  readonly subscriberCount: number;
}

/**
 * A read-only view of a signal. Useful for exposing signals without
 * allowing external mutation (e.g. store projections, `computedFrom` results).
 */
export interface ReadonlySignal<T> {
  readonly value: T;
  subscribe(fn: Subscriber<T>): Unsubscribe;
  readonly subscriberCount: number;
}

/** Options for signal creation. */
export interface SignalOptions<T> {
  /** Custom equality function. Defaults to `Object.is`. */
  equals?: (a: T, b: T) => boolean;
}

// ── Batch machinery (module-level, shared by every signal) ──────────────────
let batchDepth = 0;
const pendingFlushes = new Set<() => void>();

/**
 * Batch multiple signal writes together. Notifications are deferred until the
 * outermost `batch()` returns, and each signal coalesces to one notification
 * with its final value. Nested batches join the outer one.
 *
 * @example
 * ```ts
 * const a = signal(1);
 * const b = signal(2);
 * batch(() => {
 *   a.set(10);
 *   b.set(20);
 * }); // each subscriber called once, with the final values
 * ```
 */
export function batch(fn: () => void): void {
  batchDepth++;
  try {
    fn();
  } finally {
    batchDepth--;
    if (batchDepth === 0) {
      // Snapshot: a flush may (legitimately) schedule further work.
      const flushes = [...pendingFlushes];
      pendingFlushes.clear();
      for (const flush of flushes) flush();
    }
  }
}

/**
 * Create a new reactive signal with an initial value.
 *
 * @example
 * ```ts
 * const count = signal(0);
 * count.subscribe(v => console.log("Count:", v));
 * count.set(1);          // logs: "Count: 1"
 * count.update(c => c + 1); // logs: "Count: 2"
 * ```
 */
export function signal<T>(initial: T, options?: SignalOptions<T>): Signal<T> {
  let value = initial;
  const subscribers = new Set<Subscriber<T>>();
  const equals = options?.equals ?? Object.is;

  // Deferred-notification bookkeeping (only active inside a batch).
  let deferred = false;
  // The value subscribers last observed; used to suppress net-zero batch changes.
  let lastNotified = initial;

  const notify = (v: T) => {
    lastNotified = v;
    // Snapshot subscribers: a subscriber (e.g. an effect) may unsubscribe during
    // iteration; iterating a live Set while mutating it would skip entries.
    for (const fn of [...subscribers]) fn(v);
  };

  const flush = () => {
    deferred = false;
    if (!equals(value, lastNotified)) {
      notify(value);
    }
  };

  return {
    get value() {
      return value;
    },

    set(newValue: T) {
      if (equals(newValue, value)) return;
      value = newValue;
      if (batchDepth > 0) {
        if (!deferred) {
          deferred = true;
          pendingFlushes.add(flush);
        }
      } else {
        notify(value);
      }
    },

    update(fn: (current: T) => T) {
      this.set(fn(value));
    },

    subscribe(fn: Subscriber<T>): Unsubscribe {
      subscribers.add(fn);
      return () => {
        subscribers.delete(fn);
      };
    },

    get subscriberCount() {
      return subscribers.size;
    },
  };
}

// ── Derived primitives (explicit dependencies) ──────────────────────────────

/** A computed signal derived from explicit dependencies. Read-only + recompute. */
export interface Computed<T> extends ReadonlySignal<T> {
  /** Force recomputation of the derived value. */
  recompute(): void;
}

/** Options for computed creation. */
export interface ComputedOptions<T> {
  /** Custom equality function for comparing computed values. Defaults to `Object.is`. */
  equals?: (a: T, b: T) => boolean;
}

/**
 * Create a computed signal that derives its value from explicit dependencies.
 * Recomputes when any dependency notifies; only re-notifies its own subscribers
 * when the derived value actually changes (per `options.equals`).
 *
 * @example
 * ```ts
 * const a = signal(1);
 * const b = signal(2);
 * const sum = computedFrom([a, b], ([av, bv]) => av + bv);
 * sum.value; // 3
 * a.set(10);
 * sum.value; // 12
 * ```
 */
export function computedFrom<TDeps extends ReadonlySignal<unknown>[], TResult>(
  deps: [...TDeps],
  compute: (values: { [K in keyof TDeps]: TDeps[K]["value"] }) => TResult,
  options?: ComputedOptions<TResult>
): Computed<TResult> {
  const subscribers = new Set<Subscriber<TResult>>();
  const equals = options?.equals ?? Object.is;
  const unsubscribes: Unsubscribe[] = [];

  const getValues = () =>
    deps.map((d) => d.value) as { [K in keyof TDeps]: TDeps[K]["value"] };

  let value = compute(getValues());

  const update = () => {
    const newValue = compute(getValues());
    if (!equals(newValue, value)) {
      value = newValue;
      for (const fn of [...subscribers]) fn(value);
    }
  };

  // Subscribe to all dependencies.
  deps.forEach((dep) => {
    unsubscribes.push(dep.subscribe(update));
  });

  return {
    get value() {
      return value;
    },

    subscribe(fn: Subscriber<TResult>): Unsubscribe {
      subscribers.add(fn);
      return () => {
        subscribers.delete(fn);
      };
    },

    get subscriberCount() {
      return subscribers.size;
    },

    recompute: update,
  };
}

/**
 * Create an effect that runs whenever any of its explicit dependencies changes.
 * Runs once immediately. If the callback returns a cleanup function, it runs
 * before each subsequent run and on disposal. Returns a disposer that
 * unsubscribes from all deps and runs the final cleanup.
 *
 * @example
 * ```ts
 * const count = signal(0);
 * const stop = effect([count], ([v]) => {
 *   console.log("count:", v);
 *   return () => console.log("cleanup");
 * });
 * count.set(1); // logs: cleanup, count: 1
 * stop();       // logs: cleanup
 * ```
 */
export function effect<TDeps extends ReadonlySignal<unknown>[]>(
  deps: [...TDeps],
  fn: (values: { [K in keyof TDeps]: TDeps[K]["value"] }) => void | (() => void)
): Unsubscribe {
  const unsubscribes: Unsubscribe[] = [];
  let cleanupFn: (() => void) | void;

  const getValues = () =>
    deps.map((d) => d.value) as { [K in keyof TDeps]: TDeps[K]["value"] };

  const run = () => {
    if (cleanupFn) cleanupFn();
    cleanupFn = fn(getValues());
  };

  // Initial run.
  run();

  // Subscribe to all dependencies.
  deps.forEach((dep) => {
    unsubscribes.push(dep.subscribe(run));
  });

  return () => {
    unsubscribes.forEach((unsub) => unsub());
    if (cleanupFn) cleanupFn();
  };
}
